import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X } from 'lucide-react';
import stripeService, { getStripe } from '../services/stripe';
import { ThemeContext } from '../context/ThemeContext';
import PoweredByStripe from './PoweredByStripe';

// TODO: dedupe ErrorBanner + appearance with CheckoutPage.js once there are 3+ Stripe call sites.
const ErrorBanner = ({ message }) => message ? (
  <div className="rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4">
    <p className="text-sm text-red-700 dark:text-red-300">{message}</p>
  </div>
) : null;

// Module-level promise so React StrictMode's double-mount reuses one SetupIntent.
// Cleared after a successful update, on retry, and on a failed create — so the next
// open creates a fresh intent. An unconfirmed intent is safely reused across reopens.
let pendingSetup = null;
function getSetupClientSecret() {
  if (!pendingSetup) {
    pendingSetup = stripeService.createSetupIntent()
      .then((d) => d.clientSecret)
      .catch((e) => { pendingSetup = null; throw e; });
  }
  return pendingSetup;
}
function clearPendingSetup() { pendingSetup = null; }

const CardForm = ({ onSuccess, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setError(null);

    const result = await stripe.confirmSetup({
      elements,
      // Fallback only — card 3DS resolves inline; a redirect-based confirmation would land
      // back here and be finalized by the return-handler in SettingsPage.
      confirmParams: { return_url: `${window.location.origin}/account/settings?tab=payment` },
      redirect: 'if_required'
    });

    if (result.error) {
      setError(result.error.message);
      setSubmitting(false);
      return;
    }
    if (result.setupIntent?.status !== 'succeeded') {
      setError('Card setup did not complete. Please try again.');
      setSubmitting(false);
      return;
    }

    try {
      await stripeService.setDefaultPaymentMethod(result.setupIntent.id);
    } catch (err) {
      // The card is attached and the setup_intent.succeeded webhook will still set it as default,
      // but this SetupIntent is now consumed — drop it so a reopen creates a fresh one.
      clearPendingSetup();
      setError(err.response?.data?.message || 'Could not save the new card. Please try again.');
      setSubmitting(false);
      return;
    }
    clearPendingSetup();
    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement
        options={{
          layout: { type: 'accordion', defaultCollapsed: false, radios: 'never' },
          wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
          paymentMethodOrder: ['card'],
          terms: { card: 'never' }
        }}
        onChange={(e) => setComplete(e.complete)}
      />
      <ErrorBanner message={error} />
      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || !complete}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
        >
          {submitting ? (
            <>
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
              Saving…
            </>
          ) : 'Save card'}
        </button>
      </div>
      <PoweredByStripe className="pt-1" />
    </form>
  );
};

const UpdateCardModal = ({ onClose, onUpdated }) => {
  const { darkMode } = useContext(ThemeContext);
  const [clientSecret, setClientSecret] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  const stripeRef = useRef(null);
  if (!stripeRef.current) stripeRef.current = getStripe();

  useEffect(() => {
    let active = true;
    setClientSecret(null);
    setLoadError(null);
    getSetupClientSecret()
      .then((cs) => { if (active) setClientSecret(cs); })
      .catch(() => { if (active) setLoadError("We couldn't start the card update. Please try again."); });
    return () => { active = false; };
  }, [attempt]);

  const appearance = useMemo(() => ({
    theme: darkMode ? 'night' : 'stripe',
    labels: 'above',
    variables: {
      colorPrimary: '#2563EB',
      colorBackground: darkMode ? '#374151' : '#FFFFFF',
      colorText: darkMode ? '#F9FAFB' : '#111827',
      colorTextSecondary: darkMode ? '#9CA3AF' : '#6B7280',
      colorTextPlaceholder: '#9CA3AF',
      colorDanger: darkMode ? '#F87171' : '#DC2626',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSizeBase: '14px',
      borderRadius: '8px',
      focusBoxShadow: '0 0 0 2px #3B82F6'
    },
    rules: {
      '.Input': { borderColor: darkMode ? '#4B5563' : '#D1D5DB' },
      '.AccordionItem': {
        backgroundColor: darkMode ? '#1F2937' : '#FFFFFF',
        border: 'none',
        boxShadow: 'none',
        paddingLeft: '0px',
        paddingRight: '0px'
      }
    }
  }), [darkMode]);

  const retry = () => {
    clearPendingSetup();
    setAttempt((n) => n + 1);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Update payment method</h3>

        {loadError ? (
          <div className="text-center py-6">
            <p className="text-red-600 dark:text-red-400 mb-4">{loadError}</p>
            <button onClick={retry} className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500">
              Try again
            </button>
          </div>
        ) : !clientSecret ? (
          <div className="py-10 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <Elements key={attempt} stripe={stripeRef.current} options={{ clientSecret, appearance }}>
            <CardForm onSuccess={onUpdated} onClose={onClose} />
          </Elements>
        )}
      </div>
    </div>
  );
};

export default UpdateCardModal;
